import React, { useMemo, RefObject, useCallback } from 'react';

import { Outlet, useOutletContext } from 'react-router-dom';

import { type NavMenuItem } from 'types';
import { type User, type Venue } from 'generated/graphql';

import { Navigation } from 'components/Navigation';
import { MobileNavigation } from 'components/MobileNavigation';
import { Footer } from 'components/Footer/Footer';

import { getNavMenuItems, getSettingsOptions } from 'utils';
import { useAuth0 } from '@auth0/auth0-react';
import { useApolloClient } from '@apollo/client';

export type ContextType = {
  currentVenue?: Venue;
  currentUser?: User;
  isLoading?: boolean;
};

export const useVenueContext = (): ContextType => {
  return useOutletContext<ContextType>();
};
interface Props {
  transitionContainerRef: RefObject<HTMLDivElement>;
  currentUser?: User;
  currentVenue?: Venue;
  isLoading?: boolean;
  venueId?: string;
}

const Main: React.FC<Props> = ({ transitionContainerRef, currentUser, currentVenue, isLoading, venueId }) => {
  const { logout } = useAuth0();
  const client = useApolloClient();

  const logoutAndClearCache = useCallback(async () => {
    await client.resetStore();

    await logout({ logoutParams: { federated: true } });
  }, [client, logout]);

  const menuItems = useMemo((): NavMenuItem[] => {
    return getNavMenuItems(venueId);
  }, [venueId]);

  return (
    <div ref={transitionContainerRef}>
      <Navigation
        menuItems={menuItems}
        currentUser={currentUser}
        venueId={venueId}
        settings={getSettingsOptions(logoutAndClearCache, venueId)}
        isLoading={isLoading}
      />
      <main className="transition-container">
        <Outlet context={{ currentVenue, isLoading, currentUser }} />
      </main>
      <MobileNavigation menuItems={menuItems} />
      <Footer venueId={venueId} />
    </div>
  );
};

export default Main;
